/**
 * The run's UPPER tier — the wall cabinets hung above the counter.
 *
 * Extracted from `fillRun.js` (2026-09-19) as the first step of making uppers
 * a tier of their own: see claude-docs/2026-09-19-upper-cabinet-tier-design.md.
 * Pure — every function here returns what it built and mutates nothing it was
 * handed; `fillRun` tags the boxes and folds them into its state.
 *
 * Two things decide where an upper hangs:
 *
 *  - Along a straight stretch the uppers MIRROR the lower bays, so the two
 *    tiers' seams line up (`mirroredBoxes`).
 *  - At a CORNER the tier decides for itself (`planUpperCorners`). An upper is
 *    12" deep and meets different obstacles than a base — a high-sill window
 *    lets the corner base through and blocks the upper above it; a blind
 *    corner's trimmed leg only has to clear an UPPER's depth — so inheriting
 *    the lower corner wholesale left bare wall wherever the two should differ.
 *    The stretch between the upper's corner bound and the lower's is the
 *    upper tier's own to pack (`cornerBoxesFor`).
 */

const { SPAN_EPS } = require('./spanPacking');
const {
  segmentFillBounds,
  obliqueStop,
  cornerIndexForUnit,
} = require('./corners');
const { hoodSkySpans } = require('./applianceInstall');
const { withHoodClearance } = require('./hoodClearance');
const {
  allOpeningReservationsForSegment,
  adjacentOpeningReservationsForSegment,
  blindOrEmptyCorner,
} = require('./openings');
const {
  familyFillOpts,
  familyOf,
  upperCornerBlindLength,
  upperCornerBlindSet,
} = require('./families');
const { snapUpperHeightIn, fitUpperWidthIn } = require('./vendorSizes');
const { previousSpansOf } = require('./overrideHomes');
const {
  upperProbe,
  bayHeightFor,
  runUpperHeightIn,
  offeredUpperHeightIn,
  fitsUnderCeiling,
  roomInches,
  ceilingReason,
} = require('./upperHeights');
const { blindSizeReport } = require('./cornerChoice');
const {
  wantsGlass,
  glassFillOpts,
  glazableWidthsIn,
  glassUnavailableReason,
  solidUppersWarning,
  isSilentGlassRefusal,
} = require('./glassUppers');
const { effectiveAppearance } = require('./bayOverrides');
const {
  applyUpperOverrides,
  packIndependentSpans,
} = require('./upperBayOverrides');
const { WALL_DRAWERS } = require('./wallDrawers');
const { RACKS } = require('./racks');
const { rackStackPlan, rackCabinetFrontOf } = require('./rackStacks');
const { valanceBoardOf, valanceStyleFor } = require('./valanceBays');
const {
  solveModuleSequence,
  solveEqualModuleSequence,
  widestModuleWithin,
  widestModuleIn,
  MAX_MODULE_IN,
  MIN_FILLER_IN,
} = require('./moduleSolver');
const {
  buildUpperCabinetForSegment,
} = require('../kitchen-assembly/buildUpperCabinet');
const {
  worldToWallParam,
  getWallAxes,
} = require('../kitchen-assembly/wallMath');
const { upperCornerLegs } = require('../kitchen-assembly/upperCornerLegs');
const { MIN_CORNER_LEAF } = require('../kitchen-assembly/constants');
const {
  UPPER_CORNER_BLIND_MIN_IN,
  blindSizeChoices,
  fitsBlindSize,
} = require('../kitchen-assembly/cornerBlind');
const {
  recordedUpperTreatment,
  optionIdOf,
  gradeUpperCorner,
  planFromUpperTreatment,
} = require('./upperCornerTreatment');
const { lFootprint } = require('../geometry/objectFootprint');
const {
  MIN_STANDALONE_CABINET_WIDTH,
  UPPER_CABINET_DIMENSIONS,
  UPPER_OVER_OPENING_MAX_TOP,
  OPENING_BUFFER,
  INCHES_TO_METERS,
} = require('../kitchen-assembly/constants');

/** The box one leg's upper sweeps along the other wall at a corner: 12". */
const UPPER_DEPTH = UPPER_CABINET_DIMENSIONS.depth;
/** The widest wall cabinet the run's family sells (`widestModuleIn`), metres. */
const maxModule = (fillOpts) => widestModuleIn(fillOpts) * INCHES_TO_METERS;
const MIN_FILLER = MIN_FILLER_IN * INCHES_TO_METERS;
/** The narrowest blind corner wall cabinet sold: 12" buried, a 12" door. */
const MIN_BLIND_UNIT = UPPER_CORNER_BLIND_MIN_IN * INCHES_TO_METERS;

/**
 * The lower-bay kinds no upper hangs over: a hood hangs over a range or
 * cooktop, a fridge / tall unit reaches the upper band itself, and an opening
 * or a clearance is wall (or floor) the layout holds open.
 */
const NO_UPPER_OVER_KINDS = [
  'range',
  'cooktop', // the hood hangs here
  'fridge',
  'tallUnit',
  'opening',
  'clearance',
];

/** Does this lower bay keep the upper band above it clear? */
const blocksUpperBand = (bay) =>
  !!bay.tall || NO_UPPER_OVER_KINDS.includes(bay.kind);

/**
 * The floor-standing bodies that rise into the upper band — a fridge, a tall
 * unit, a tall filler (`upperKeepOuts` names it a `tallUnit`) — as opposed to
 * an opening, a clearance or a hood's sky, which are no body at all.
 */
const BAND_BODY_KINDS = ['fridge', 'tallUnit'];

/**
 * Whether the keep-out nearest a corner leg's vertex is such a body standing
 * IN the corner: within an upper's depth of the vertex, where the other leg's
 * uppers would hang against its side.
 */
const standsInCorner = (near) =>
  near.d < UPPER_DEPTH - SPAN_EPS && BAND_BODY_KINDS.includes(near.kind);

/** `[s, e]` minus every `{lo, hi}` in `ranges`, as sorted disjoint pieces. */
function freePieces(s, e, ranges) {
  let free = [[s, e]];
  for (const r of ranges) {
    const next = [];
    for (const [a, b] of free) {
      if (r.hi <= a || r.lo >= b) {
        next.push([a, b]);
        continue;
      }
      if (r.lo > a) next.push([a, Math.min(r.lo, b)]);
      if (r.hi < b) next.push([Math.max(r.hi, a), b]);
    }
    free = next;
  }
  return free
    .filter(([a, b]) => b - a > SPAN_EPS)
    .sort((p, q) => p[0] - q[0])
    .map(([a, b]) => ({ start: a, end: b }));
}

/**
 * The widest run of `[s, e]` left after subtracting `ranges`, or null if the
 * ranges cover it entirely. Used to TRIM an upper to the wall it may actually
 * hang on rather than dropping it whole — see `mirroredBoxes`.
 */
function widestGap(s, e, ranges) {
  let best = null;
  for (const p of freePieces(s, e, ranges)) {
    if (!best || p.end - p.start > best.end - best.start) best = p;
  }
  return best;
}

/**
 * What stands in the UPPER band on a segment, as `{lo, hi}` param ranges.
 *
 *  - `openings`: every opening on this wall (windows AND doors), adjacent-wall
 *    openings near the corner, and the span a range HOOD claims beyond the bay
 *    below it — a chosen hood PRODUCT keeps its own size, so a wider one
 *    overhangs into its neighbours and they must give way. This is what a
 *    MIRRORED upper is trimmed against; the lower bays it mirrors already
 *    stand clear of everything else.
 *  - `all`: those, plus the lower bays that reach or claim the band
 *    (`blocksUpperBand`). What a corner STRETCH and a tier packed on its own
 *    read — neither has a lower bay to have done that work for it.
 *  - `low`: the openings an upper does NOT give way to, because their top is
 *    at or below `UPPER_OVER_OPENING_MAX_TOP` off the floor (user ruling
 *    2026-09-19). They are in neither list above: the uppers run across them,
 *    and each box standing over one is raised to clear its head
 *    (`buildUpperBox`).
 *  - `cornerAll`: `all` plus `low`. What the corner PLAN reads — an upper L is
 *    one unit on a fixed band, so it still gives way to any opening in its
 *    reach.
 */
function upperKeepOuts(run, segIndex, ctx, lowerBays, hoodSky) {
  // Each range names what it IS, in `fillerEnd.js`'s vocabulary: an upper
  // tier packed on its own reads it to decide which end of a span a scribe
  // dies into (`independentSpans`). Nothing else looks past `lo` / `hi`.
  const lowTopY = (ctx.floorY ?? 0) + UPPER_OVER_OPENING_MAX_TOP + SPAN_EPS;
  const onThisWall = allOpeningReservationsForSegment(run, segIndex, ctx).map(
    (r) => ({ ...r, kind: 'opening' })
  );
  const low = onThisWall.filter((r) => r.topY <= lowTopY);
  const openings = [
    ...onThisWall.filter((r) => r.topY > lowTopY),
    ...adjacentOpeningReservationsForSegment(run, segIndex, ctx),
  ]
    .map((r) => ({ ...r, kind: 'opening' }))
    .concat(
      hoodSky
        .filter((h) => h.segIndex === segIndex)
        .map((h) => ({ ...h, kind: 'range' }))
    );
  const lowerTier = lowerBays
    .filter((b) => b.segIndex === segIndex && blocksUpperBand(b))
    .map((b) => ({
      lo: Math.min(b.start, b.end),
      hi: Math.max(b.start, b.end),
      // A tall filler is a scribe grown to a tall unit's height.
      kind: b.tall && b.kind === 'filler' ? 'tallUnit' : b.kind,
    }));
  const all = [...openings, ...lowerTier];
  return { openings, all, low, cornerAll: [...all, ...low] };
}

/**
 * A corner LEG: segment `segIndex` seen from the vertex at its `end`
 * ('start' | 'end'). `away` is the param direction leading off from the
 * vertex, so a distance `d` along the leg is the param `vertex + away * d`.
 */
function legOf(run, segIndex, end) {
  const seg = run.segments[segIndex];
  const vertex = end === 'end' ? seg.endParam : seg.startParam;
  const other = end === 'end' ? seg.startParam : seg.endParam;
  return { segIndex, end, vertex, away: Math.sign(other - vertex) || 1 };
}

/** The param range covering distances `[dFrom, dTo]` along a leg. */
function legRange(leg, dFrom, dTo) {
  const a = leg.vertex + leg.away * dFrom;
  const b = leg.vertex + leg.away * dTo;
  return { start: Math.min(a, b), end: Math.max(a, b) };
}

/**
 * How far a leg is clear from its vertex: the distance to the nearest keep-out
 * ahead of it, 0 when one straddles the vertex, Infinity when there is none.
 */
function clearRunFromVertex(leg, keepOuts) {
  return nearestKeepOut(leg, keepOuts).d;
}

/**
 * The keep-out that ENDS a leg's clear run: how far it stands from the vertex
 * (`clearRunFromVertex`'s number), and what it is — its `kind`, and the
 * opening's id where it is one — so an option that does not fit can say why.
 */
function nearestKeepOut(leg, keepOuts) {
  let best = { d: Infinity, kind: null, openingId: null };
  for (const r of keepOuts) {
    const d1 = (r.lo - leg.vertex) * leg.away;
    const d2 = (r.hi - leg.vertex) * leg.away;
    if (Math.max(d1, d2) <= SPAN_EPS) continue; // wholly behind the vertex
    const d = Math.max(0, Math.min(d1, d2));
    if (d < best.d)
      best = { d, kind: r.kind ?? null, openingId: r.openingId ?? null };
  }
  return best;
}

/**
 * How far an L-shaped corner unit reaches along a leg, from the vertex: the
 * furthest of its plan corners, projected onto the leg's wall. Read from the
 * unit's FOOTPRINT rather than `alongA` / `alongB`, because which of those is
 * which leg depends on how the builder folded the box into the corner. Null
 * when the unit carries no L to read.
 */
function unitReachAlongLeg(unit, leg, wall) {
  const corners = lFootprint(unit);
  if (!corners) return null;
  let reach = 0;
  for (const c of corners) {
    const p = worldToWallParam(wall, c);
    if (p == null) return null;
    reach = Math.max(reach, (p - leg.vertex) * leg.away);
  }
  return reach;
}

/** A leg's corner trim, read out of a per-segment `{ start, end }` table. */
const trimOf = (trims, leg) => trims[leg.segIndex]?.[leg.end] ?? 0;

/**
 * The upper tier's OWN decision at each corner of the run.
 *
 *  - `cabinet`: an upper L hangs here, on the legs `upperCornerLegs` gives it:
 *    the standard 24", each shrunk to its wall's clear run, refused when
 *    either cannot show a door leaf. It hangs over WHATEVER the lower corner
 *    is — an L, a blind box, an empty corner (user ruling 2026-09-19,
 *    replacing 2026-09-13's "only over a lower L", which was made when the
 *    upper L was 36" a leg and rarely fitted anywhere else). Each leg's trim
 *    is the L's reach along it, so the wall between a 24" upper L and the
 *    lower's bound is a corner stretch like any other.
 *  - `incumbent`: the run already owns an upper L here that the fill will not
 *    rebuild — `materializeRun`'s carry-forward keeps it, and it may stand on
 *    legs this plan would not have chosen. Each leg stops where that L's leg
 *    ends, so nothing is hung through it. An L that cannot be read leaves the
 *    corner exactly as the lower has it.
 *  - `blind` / `empty`: no L fits, so the same two rungs the lower corner
 *    falls to (`blindOrEmptyCorner`), read against the UPPER band's keep-outs
 *    at an upper's 12" depth. `blind`: the leg with the longer clear run takes
 *    the corner alone, to the vertex. `empty`: something stands within 12" of
 *    the vertex and neither leg reaches it. (The PLAN's own blind corner never
 *    has a cabinet butting the extending leg's face — that needs 21" clear on
 *    one leg and 24" on the other, and with 21" on both an L fits. A blind
 *    corner the USER chose does, because it can be chosen where an L fits, so
 *    the box it buries is a blind cabinet: `markBlindCornerBoxes`.)
 *  - `oblique`: each leg stops where a 12"-deep box meets the other on the
 *    bisector — unless the lower stop was pushed past its own bisector stop
 *    (it cleared a body parked at the vertex, or absorbed a sliver), in which
 *    case the upper keeps the lower's stop rather than reason about that body
 *    again.
 *
 * A corner with a peninsula leg keeps the lower's trims: there is no wall
 * above an arm, and the wall leg's uppers stop where they always have.
 *
 * Two outputs describe the same decision from two sides. `trims` is where
 * each leg's OWN packing starts (never past the lower's trim — a leg the lower
 * runs to the vertex has no corner stretch to pack). `claims` is the wall the
 * corner has taken that a MIRRORED upper must give way to: an L's reach along
 * both legs, and at a blind or empty corner the 12" of a leg that another
 * leg's body — or an opening's casing — stands against. Without the claims a
 * lower leg that runs to the vertex (a single-sided unit) mirrors an upper
 * straight through the L above it.
 *
 * @param {Object} lower `{ cornerTrims, cabinetCorners, corners }` — the lower
 *   tier's per-segment trims, its L corners, and its per-corner decision
 * @param {{all: {lo, hi}[]}[]} keepOutsBySeg `upperKeepOuts` per segment
 * @returns {{ trims: {start, end}[], claims: {lo, hi}[][],
 *   lCorners: Map<number, {reachA: number, reachB: number}>,
 *   corners: {cornerIndex, mode, extend?}[] }} `claims` is indexed by
 *   segment. `lCorners`: the corners that carry an upper L and how far it
 *   reaches along segment i's leg and segment i+1's — what
 *   `buildCornerUppers` builds.
 */
function planUpperCorners(run, ctx, lower, keepOutsBySeg) {
  const trims = run.segments.map((_, i) => ({
    start: lower.cornerTrims[i]?.start ?? 0,
    end: lower.cornerTrims[i]?.end ?? 0,
  }));
  const claims = run.segments.map(() => []);
  const claim = (leg, reach) => {
    if (!(reach > SPAN_EPS)) return;
    const r = legRange(leg, 0, reach);
    claims[leg.segIndex].push({ lo: r.start, hi: r.end, kind: 'corner' });
  };
  const lCorners = new Map();
  const incumbents = new Map(
    (ctx.carriedCornerUppers || []).map((u) => [cornerIndexForUnit(run, u), u])
  );
  const corners = [];
  for (let i = 0; i < run.segments.length - 1; i += 1) {
    const lowerCorner = lower.corners.get(i);
    if (!lowerCorner) continue; // not a corner
    const segA = run.segments[i];
    const segB = run.segments[i + 1];
    if (segA.wall?.isVirtual || segB.wall?.isVirtual) continue;
    const legA = legOf(run, i, 'end');
    const legB = legOf(run, i + 1, 'start');
    const nearA = nearestKeepOut(legA, keepOutsBySeg[i].cornerAll);
    const nearB = nearestKeepOut(legB, keepOutsBySeg[i + 1].cornerAll);
    const clearA = nearA.d;
    const clearB = nearB.d;
    // The L is built on orthogonal axes; an oblique joint is served by stops.
    const oblique = lowerCorner.mode === 'oblique';
    const lLegs = oblique ? null : upperCornerLegs(clearA, clearB);
    // What the room allows here, and what the user asked for. A choice is
    // honoured only while it fits (`upperCornerTreatment.js`); one the room no
    // longer allows falls through to the automatic ladder below and is
    // reported, never built into an opening. `cabinet` has no plan of its own:
    // it IS the ladder's first two rungs — the L, else the run's own.
    const options = gradeUpperCorner({
      clearA,
      clearB,
      blockerA: nearA.kind,
      blockerB: nearB.kind,
      blockerIdA: nearA.openingId,
      blockerIdB: nearB.openingId,
      oblique,
      hasIncumbent: incumbents.has(i),
    });
    const recorded = recordedUpperTreatment(run, i);
    const requested = optionIdOf(recorded);
    const honoured =
      !!requested && options.some((o) => o.id === requested && o.feasible);
    const chosen = honoured ? planFromUpperTreatment(recorded) : null;
    const report = (p) => ({
      cornerIndex: i,
      mode: p.mode,
      extend: p.extend,
      wallIds: [segA.wall?.id ?? null, segB.wall?.id ?? null],
      requested,
      honoured: requested ? honoured : null,
      options,
    });
    let plan;
    if (chosen) {
      plan = chosen;
      claim(legA, plan.trimA);
      claim(legB, plan.trimB);
    } else if (lLegs) {
      lCorners.set(i, { reachA: lLegs.alongA, reachB: lLegs.alongB });
      plan = { mode: 'cabinet', trimA: lLegs.alongA, trimB: lLegs.alongB };
      claim(legA, plan.trimA);
      claim(legB, plan.trimB);
    } else if (incumbents.has(i)) {
      const unit = incumbents.get(i);
      const reachA = unitReachAlongLeg(unit, legA, segA.wall);
      const reachB = unitReachAlongLeg(unit, legB, segB.wall);
      if (reachA == null || reachB == null) continue; // unreadable: lower's trims
      plan = { mode: 'incumbent', trimA: reachA, trimB: reachB };
      claim(legA, reachA);
      claim(legB, reachB);
    } else if (oblique) {
      const stop = obliqueStop(segA, segB, UPPER_DEPTH);
      const stopFor = (leg) => {
        const lowerStop = trimOf(lower.cornerTrims, leg);
        return lowerStop > lowerCorner.symmetric + SPAN_EPS ? lowerStop : stop;
      };
      plan = { mode: 'oblique', trimA: stopFor(legA), trimB: stopFor(legB) };
    } else {
      plan = blindOrEmptyCorner(clearA, clearB, UPPER_DEPTH);
      // The leg that does not take the corner gives up the 12" the other
      // leg's body stands against; at an empty corner both do.
      claim(legA, plan.trimA);
      claim(legB, plan.trimB);
    }
    trims[i].end = Math.min(plan.trimA, trimOf(lower.cornerTrims, legA));
    trims[i + 1].start = Math.min(plan.trimB, trimOf(lower.cornerTrims, legB));
    // A TALL body standing in the corner on one leg is a base's depth deep,
    // not an upper's, and it reaches the band: the other leg's uppers stop
    // where that leg's BASES do, at its face. Trimmed by an upper's depth, the
    // stretch between was packed with an upper hung into the tall cabinet's
    // side (26767201: 144 sq in, 2026-10-23).
    if (standsInCorner(nearB)) {
      trims[i].end = Math.max(trims[i].end, trimOf(lower.cornerTrims, legA));
      claim(legA, trims[i].end);
    }
    if (standsInCorner(nearA)) {
      trims[i + 1].start = Math.max(
        trims[i + 1].start,
        trimOf(lower.cornerTrims, legB)
      );
      claim(legB, trims[i + 1].start);
    }
    corners.push(report(plan));
  }
  return { trims, claims, lCorners, corners };
}

/**
 * Pack one leg's CORNER STRETCH — the wall between the upper's corner bound
 * and the lower's, which no lower bay covers — as boxes, walking out from the
 * vertex.
 *
 * Each free piece of the stretch (what the keep-outs leave) is solved by the
 * run's family like any other span. Its remainder is a SCRIBE, and a scribe
 * dies into the corner, so it goes on the vertex side with the modules packed
 * against the run.
 *
 * A piece too short for any module emits nothing here: a filler with no
 * cabinet of its own beside it is a panel on bare wall. It comes back as a
 * `sliver` when it reaches the lower's bound, where the first mirrored upper
 * can absorb it (`absorbSlivers`).
 *
 * @param {Object} leg `legOf`
 * @param {number} dFrom distance from the vertex where the stretch begins
 * @param {number} dTo   …and where it ends (the lower tier's bound)
 * @returns {{ boxes: {start, end, filler}[], slivers: {start, end, farEdge}[] }}
 */
/**
 * Mark the box a blind corner BURIES part of.
 *
 * A blind corner runs one leg's uppers to the vertex and stops the other an
 * upper's depth short — so where the other leg has a cabinet there, it stands
 * across the first 12" of the extending leg's face. A plain two-door box in
 * that spot shows half a door and hides a handle inside its neighbour.
 *
 * The first cut of this tier had a blind panel and removed it, reasoning that
 * the case could not arise: a blind corner was only ever the PLAN's answer
 * where no L fitted, which means something stood near the vertex, which means
 * nothing butted that face. The user's choice (`upperCornerTreatment.js`)
 * broke that premise — a blind corner can now be chosen where both legs are
 * clear — and the first person to try it saw the buried door (2026-09-21).
 *
 * So it is decided from what is THERE, not from how the corner was chosen: a
 * box on the extending leg is blind over whatever part of it lies within an
 * upper's depth of the vertex, IF a box on the other leg actually stands at
 * that depth. Its front is then a blank panel over the buried part and a door
 * on the rest (`buildUpperBox`); a box whose remainder could not show a real
 * door leaf is blank all over — which takes a SHALLOW run, whose corner box is
 * its own measured depth. At the standard depth the box at the vertex is
 * always one module of 21" or more (a corner stretch never exceeds the 36" L
 * footprint, and a mirrored blind unit splits evenly), so nothing re-orders
 * the packing for it: an ordering rule was written, no mutation of it could
 * fail a test, and it was taken out again.
 *
 * @param {Object[][]} boxesBySeg the planned boxes per segment — MUTATED: the
 *   blind box gains `blind: { atStart: boolean, width: number }`
 */
function markBlindCornerBoxes(run, plan, boxesBySeg) {
  for (const corner of plan.corners) {
    if (corner.mode !== 'blind') continue;
    const legA = legOf(run, corner.cornerIndex, 'end');
    const legB = legOf(run, corner.cornerIndex + 1, 'start');
    const [taking, other] = corner.extend === 'A' ? [legA, legB] : [legB, legA];
    const stands = (box) => box && !box.none;
    // Does the other leg have a box standing AT an upper's depth — i.e. whose
    // near edge is on the extending leg's front face?
    const face = other.vertex + other.away * UPPER_DEPTH;
    const butting = (boxesBySeg[other.segIndex] || []).some(
      (b) =>
        stands(b) &&
        Math.abs((other.away > 0 ? b.start : b.end) - face) <= SPAN_EPS
    );
    if (!butting) continue;
    // The user's SIZE for the unit rides on the same choice as the leg. It
    // needs no "only while the leg choice stands" guard: the one grade that
    // fails a blind choice — a leg under an upper's depth — is the same thing
    // that stops a box standing across the other leg's face, so a lapsed
    // choice never reaches this line (pinned in the suite).
    const wanted =
      recordedUpperTreatment(run, corner.cornerIndex)?.length ?? null;
    const size = sizeBlindUnit(
      run,
      taking,
      boxesBySeg[taking.segIndex] || [],
      wanted
    );
    if (size) corner.size = size;
    const buried = legRange(taking, 0, UPPER_DEPTH);
    for (const box of boxesBySeg[taking.segIndex] || []) {
      if (!stands(box) || box.filler || box.openShelf) continue;
      const width =
        Math.min(box.end, buried.end) - Math.max(box.start, buried.start);
      if (!(width > SPAN_EPS)) continue;
      // The vertex is at the box's LOW-param end when the leg heads up-param.
      box.blind = { atStart: taking.away > 0, width };
    }
  }
}

/**
 * Give the blind box the width a blind corner WALL cabinet is SOLD at.
 *
 * What the packer left at the vertex is whatever filled the wall to the first
 * seam — 36" over a corner base, since one 36" module is the tidiest way to
 * fill 36" of wall. But a 36" blind wall cabinet is 12" of buried box and a
 * 24" door onto 12" of depth, where the standard is 24" wide with the rest of
 * the wall given to an ordinary cabinet (user ruling 2026-09-21). So the wall
 * from the vertex to that seam is re-cut: a unit from the family's blind sizes
 * (`upperCornerBlindLength` — 24" first, growing only to swallow a leftover too
 * narrow to be a cabinet), then the leftover as a cabinet of its own; a
 * leftover too narrow for that stays a scribe at the VERTEX, where the other
 * leg's cabinet hides it.
 *
 * The seam is the far edge of the box that reaches the unit — the smallest
 * sold, or the one the user CHOSE (`wanted`, metres) — so the uppers still
 * line up with the lowers beyond it. Every size the family sells is graded
 * against the whole plain stretch from the vertex, and a chosen size is built
 * only while it fits; the report of that is returned for the panel. Boxes the
 * user has edited are left exactly as they are — they are only marked blind.
 *
 * @param {Object[]} boxes the leg's planned boxes — MUTATED in place
 * @returns {Object|undefined} the size report (`cornerChoice.blindSizeReport`)
 *   when a unit was cut
 */
function sizeBlindUnit(run, taking, boxes, wanted = null) {
  const dist = (param) => (param - taking.vertex) * taking.away;
  const near = (b) => Math.min(dist(b.start), dist(b.end));
  const far = (b) => Math.max(dist(b.start), dist(b.end));
  // The plain, contiguous, fill-owned boxes from the vertex out, and the seam
  // each one ends at.
  const fromVertex = boxes
    .filter((b) => far(b) > SPAN_EPS)
    .sort((a, b) => near(a) - near(b));
  const plain = [];
  let reach = 0;
  for (const b of fromVertex) {
    if (near(b) > reach + SPAN_EPS) break; // a gap: a keep-out stands here
    if (b.none || b.openShelf || b.override || b.fixedWidth) break;
    plain.push(b);
    reach = far(b);
  }
  const set = upperCornerBlindSet(run);
  const choices = blindSizeChoices(set, reach / INCHES_TO_METERS);
  const wantedFits =
    wanted != null &&
    fitsBlindSize(set, wanted / INCHES_TO_METERS, reach / INCHES_TO_METERS);
  // The stretch to re-cut: to the first seam at or past the unit.
  const need = wantedFits ? wanted : MIN_BLIND_UNIT;
  const stretch = [];
  let seam = 0;
  for (const b of plain) {
    stretch.push(b);
    seam = far(b);
    if (seam >= need - SPAN_EPS) break;
  }
  if (seam < need - SPAN_EPS) return undefined;

  const size = wantedFits
    ? wanted
    : upperCornerBlindLength(run, seam / INCHES_TO_METERS) * INCHES_TO_METERS;
  if (!(size > 0)) return undefined;
  const rest = seam - size;
  const at = (dFrom, dTo, flags = {}) => {
    const r = legRange(taking, dFrom, dTo);
    return { start: r.start, end: r.end, filler: false, ...flags };
  };
  // A leftover too narrow for a cabinet stands at the VERTEX, which pushes the
  // unit off it by that much — and buries that much less of it.
  const scribe = rest < MIN_STANDALONE_CABINET_WIDTH - SPAN_EPS;
  const recut = !scribe
    ? [at(0, size), at(size, seam)]
    : [
        ...(rest >= MIN_FILLER
          ? [at(0, rest, { filler: true, corner: true })]
          : []),
        at(rest, seam),
      ];
  for (const b of stretch) boxes.splice(boxes.indexOf(b), 1);
  boxes.push(...recut);
  return blindSizeReport(choices, {
    built: size,
    requested: wanted,
    honoured: wanted != null ? wantedFits : null,
    door: size - Math.max(0, UPPER_DEPTH - (scribe ? rest : 0)),
  });
}

/**
 * A blind box's front, as the renderer's columns run: local +X is the host
 * wall's own direction, so the buried part is the FIRST column when the vertex
 * is at the box's low-param end. The door's pull goes on the edge away from
 * the buried part — the reachable one, as on the lower tier's blind unit.
 */
function blindColumns(width, blind) {
  const leaf = width - blind.width;
  if (leaf < MIN_CORNER_LEAF) return [{ type: 'false-panel', width }];
  const panel = { type: 'false-panel', width: blind.width };
  const door = {
    type: 'full-height-doors',
    width: leaf,
    // ONE door, as on the lower tier's blind unit: it is never over 24" (the
    // widest box is 36", 12" of it buried), and a pair here would put a second
    // pull hard against the panel.
    doors: 1,
    handleOnRight: blind.atStart,
  };
  return blind.atStart ? [panel, door] : [door, panel];
}

function packCornerStretch(leg, dFrom, dTo, keepOuts, fillOpts) {
  const out = { boxes: [], slivers: [] };
  if (dTo - dFrom <= SPAN_EPS) return out;
  const stretch = legRange(leg, dFrom, dTo);
  const farEdge = leg.vertex + leg.away * dTo;
  for (const piece of freePieces(stretch.start, stretch.end, keepOuts)) {
    const { modules, filler } = solveModuleSequence(
      piece.end - piece.start,
      fillOpts
    );
    if (!modules.length) {
      const atFar =
        Math.abs((leg.away > 0 ? piece.end : piece.start) - farEdge) <=
        SPAN_EPS;
      if (atFar) out.slivers.push({ ...piece, farEdge, scribe: true });
      continue;
    }
    // Walk out from the vertex side of the piece: scribe first, then modules.
    const scribe = filler ? filler.widthMeters : 0;
    let d = 0;
    const take = (width, isFiller, flags = {}) => {
      const a = leg.away > 0 ? piece.start + d : piece.end - d - width;
      out.boxes.push({ start: a, end: a + width, filler: isFiller, ...flags });
      d += width;
    };
    if (scribe >= MIN_FILLER) take(scribe, true, { corner: true });
    else d += scribe;
    // Smallest module nearest the corner, the widest against the run.
    for (const m of [...modules].reverse()) take(m.widthMeters, false);
  }
  return out;
}

/**
 * The boxes the corners add on each segment: every corner leg's stretch —
 * from where the upper tier's own packing starts (`plan.trims`) to the lower
 * tier's bound — packed against the upper band's keep-outs AND the corner's
 * own claims, so a stretch never packs into an L's leg.
 *
 * @returns {{ boxes: Object[][], slivers: Object[][] }} each indexed by segment
 */
function cornerBoxesFor(run, plan, lower, keepOutsBySeg) {
  const bySeg = () => run.segments.map(() => []);
  const out = { boxes: bySeg(), slivers: bySeg() };
  const fillOpts = upperFillOpts(run);
  for (const corner of plan.corners) {
    for (const leg of [
      legOf(run, corner.cornerIndex, 'end'),
      legOf(run, corner.cornerIndex + 1, 'start'),
    ]) {
      const packed = packCornerStretch(
        leg,
        trimOf(plan.trims, leg),
        trimOf(lower.cornerTrims, leg),
        [...keepOutsBySeg[leg.segIndex].all, ...plan.claims[leg.segIndex]],
        fillOpts
      );
      out.boxes[leg.segIndex].push(...packed.boxes);
      out.slivers[leg.segIndex].push(...packed.slivers);
    }
  }
  return out;
}

/** What a run's own END stands against: the wall it dies into, or nothing. */
function runEndKind(wall, param) {
  const wallLen = getWallAxes(wall)?.wallLen ?? 0;
  return Math.abs(param) <= MIN_FILLER ||
    Math.abs(param - wallLen) <= MIN_FILLER
    ? 'wall'
    : 'open-end';
}

/**
 * The clear SPANS of upper wall on a segment, for a tier packed on its own
 * (`upperSpec.align: 'independent'`): the segment between where the upper
 * tier's own packing starts at each corner (`plan.trims`), minus everything
 * standing in the band and everything a corner claimed. Each span names what
 * stands at its two ends — the thing it was cut by, or the segment's own end
 * (a corner, the wall the run dies into, or an open end).
 *
 * @returns {{ lo, hi, loBound, hiBound }[]}
 */
function independentSpans(run, segIndex, plan, lower, keepOuts) {
  const seg = run.segments[segIndex];
  const { lo, hi } = segmentFillBounds(run.segments, segIndex, plan.trims);
  if (hi - lo <= SPAN_EPS) return [];
  const taken = [...keepOuts.all, ...plan.claims[segIndex]];
  const startKind = lower.corners.has(segIndex - 1)
    ? 'corner'
    : runEndKind(seg.wall, seg.startParam);
  const endKind = lower.corners.has(segIndex)
    ? 'corner'
    : runEndKind(seg.wall, seg.endParam);
  const ascending = seg.endParam >= seg.startParam;
  const segLoKind = ascending ? startKind : endKind;
  const segHiKind = ascending ? endKind : startKind;
  const cutBy = (edge, side) =>
    taken.find((r) => Math.abs(r[side] - edge) <= SPAN_EPS)?.kind;
  return freePieces(lo, hi, taken).map((p) => ({
    lo: p.start,
    hi: p.end,
    loBound: cutBy(p.start, 'hi') ?? segLoKind,
    hiBound: cutBy(p.end, 'lo') ?? segHiKind,
  }));
}

/** A box within a millimetre of a width the family sells IS that width. */
const FAMILY_EPS = 0.001;

/**
 * The keep-outs with nothing solid in them: an opening's buffer, the sky over
 * a range. What an upper leaves beside one stays BARE — a blank panel beside
 * a window closes nothing, and the keep-out already stands 4" off the glass
 * (user ruling 2026-09-21). Anything else a mirror can be cut by is a body —
 * a corner unit, the other leg's cabinet — and a gap against a body is scribed.
 */
const OPEN_KEEP_OUTS = new Set(['opening', 'range']);

/**
 * The cabinets (metres, widest first) the run's family hangs on `width` of
 * wall. Wall one cabinet can span is ONE cabinet — the widest the family sells
 * that fits — never a tiling: 27" under a stock family tiles as 15" + 12", and
 * over a single lower bay that is two odd doors where a 24" cabinet belongs.
 * Only wall too wide for any one cabinet is covered with several.
 */
function familyModules(width, fillOpts) {
  if (width <= maxModule(fillOpts) + SPAN_EPS) {
    const one = widestModuleWithin(width, fillOpts);
    return one > 0 ? [one] : [];
  }
  return solveModuleSequence(width, fillOpts)
    .modules.map((m) => m.widthMeters)
    .sort((a, b) => b - a);
}

/** Is `width` ONE cabinet the run's family sells? */
function isFamilyWidth(width, fillOpts) {
  return Math.abs(widestModuleWithin(width, fillOpts) - width) <= FAMILY_EPS;
}

/**
 * Cut a TRIMMED mirror to widths the run's family sells.
 *
 * A mirrored upper inherits its lower bay's span, and a keep-out — a window's
 * buffer, the hood's sky, what a corner took — trims it to the wall it can
 * hang on. That used to be its width: a 36" bay beside a window hung a 32¼"
 * upper, under a Stock family that sells 12 / 15 / 18 / 24 / 30 / 36 (user
 * report 2026-09-21; 58 of 699 uppers across the 111 generated fixture
 * layouts, on the default 3" grid). A width is a catalogue entry: the box is
 * what the family fills that wall with.
 *
 * The cabinets stand against the side that is NOT cut — the lower's seam, so
 * the tiers still line up there — and what is left over goes to the cut side:
 * bare beside an opening or the hood, a scribe against a body. Cut on both
 * sides, they stand against a body if one side is one, else in the middle.
 *
 * @param {{start, end}} clear the wall the keep-outs leave this bay
 * @param {{lo: boolean, hi: boolean}} clipped which ends were cut
 * @returns {{start, end, filler?, corner?}[] | null} null when the family has
 *   no cabinet that narrow — the caller offers the wall to its neighbour
 */
function fitTrimmedToFamily(clear, clipped, keepOut, fillOpts) {
  const width = clear.end - clear.start;
  const modules = familyModules(width, fillOpts);
  if (!modules.length) return null;
  const covered = modules.reduce((sum, w) => sum + w, 0);
  const leftover = width - covered;
  if (leftover <= FAMILY_EPS) return [{ start: clear.start, end: clear.end }];

  const kindAt = (side) => {
    const edge = side === 'lo' ? clear.start : clear.end;
    const hit = keepOut.find(
      (r) => Math.abs((side === 'lo' ? r.hi : r.lo) - edge) <= SPAN_EPS
    );
    return hit?.kind ?? 'opening';
  };
  const solid = (side) => clipped[side] && !OPEN_KEEP_OUTS.has(kindAt(side));
  // How much of the leftover stands at the LOW end.
  let atLo;
  if (clipped.lo !== clipped.hi) atLo = clipped.lo ? leftover : 0;
  else if (solid('lo') !== solid('hi')) atLo = solid('lo') ? 0 : leftover;
  else atLo = leftover / 2;

  const out = [];
  const scribe = (start, end, side) => {
    if (end - start >= MIN_FILLER && solid(side))
      out.push({ start, end, filler: true, corner: true });
  };
  scribe(clear.start, clear.start + atLo, 'lo');
  // Widest against the seam; on a box cut at its LOW end that is the high one.
  const ordered = clipped.lo && !clipped.hi ? [...modules].reverse() : modules;
  let cursor = clear.start + atLo;
  for (const w of ordered) {
    out.push({ start: cursor, end: cursor + w });
    cursor += w;
  }
  scribe(cursor, clear.end, 'hi');
  return out;
}

/**
 * The MIRRORED boxes on a segment: one per lower bay, so the two tiers line up
 * edge-to-edge and inherit every constraint the lower fill already resolved.
 *
 * EVERY lower bay is a candidate (cabinets AND fillers — a filler gets a
 * matching blank upper so the upper run stays continuous, like the lower run),
 * the dishwasher gap included: a dishwasher bay is in `lowerBays` as a hard
 * piece and gets an upper like any other. A sink base does too — on a
 * windowless wall the sky above the sink is cabinet sky, and the window that
 * usually stands there is an opening keep-out (user ruling 2026-09-13; it used
 * to be skipped outright as "the sink owns that sky", which left a bare patch
 * between uppers on 3,830 corpus layouts on the first edit). Range / fridge /
 * tall / opening pieces are skipped (`blocksUpperBand`).
 *
 * Mirrored within the LOWER tier's own fill bounds only — past them, toward a
 * corner, the wall is the upper tier's own to pack (`cornerBoxesFor`).
 */
function mirroredBoxes(run, segIndex, lower, lowerBays, keepOut, fillOpts) {
  const { lo, hi } = segmentFillBounds(
    run.segments,
    segIndex,
    lower.cornerTrims
  );
  const boxes = [];
  const slivers = [];
  if (hi - lo <= SPAN_EPS) return { boxes, slivers };
  for (const b of lowerBays) {
    if (b.segIndex !== segIndex || blocksUpperBand(b)) continue;
    const s = Math.max(lo, Math.min(b.start, b.end));
    const e = Math.min(hi, Math.max(b.start, b.end));
    if (e - s <= SPAN_EPS) continue;

    // An upper has no span of its own — it inherits the lower bay's (or the
    // DW gap's), which is what makes the two line up edge-to-edge. So a
    // candidate that merely CLIPPED an opening's buffer used to lose its
    // whole upper: a 24" DW upper dropped for a 2" clip. Trim it to the wall
    // it can actually hang on instead.
    //
    // One guard keeps that from over-reaching: only a TRIMMED candidate has
    // to clear MIN_CABINET_WIDTH. An untrimmed one is emitted exactly as
    // before, so the minimum can never retire a narrow bay's upper that has
    // always been drawn.
    //
    // Fillers need no case of their own. `solveModuleSequence` emits one only
    // below MIN_MODULE_IN (9") or as a [0.25, 3") remainder, so a filler is
    // always narrower than the minimum and a trimmed one is dropped here —
    // which is what we want, since a filler upper exists to match the width
    // of the filler below it.
    const clear = widestGap(s, e, keepOut);
    if (!clear) continue;
    const clippedLo = clear.start > s + SPAN_EPS;
    const clippedHi = clear.end < e - SPAN_EPS;
    const trimmed = clippedLo || clippedHi;
    if (trimmed && clear.end - clear.start < MIN_STANDALONE_CABINET_WIDTH) {
      // Too short to stand as a cabinet of its own. Clipped on ONE side it
      // still touches the bay next door, whose upper may absorb it
      // (`absorbSlivers`); clipped on both it has nothing to join. A filler's
      // sliver is never offered: a filler upper exists to match the filler
      // below it.
      if (b.kind !== 'filler' && clippedLo !== clippedHi) {
        slivers.push({
          ...clear,
          farEdge: clippedLo ? clear.end : clear.start,
        });
      }
      continue;
    }
    // Untrimmed, the upper IS the lower bay's span — emitted exactly as it
    // always was, whatever that bay's width. Trimmed, it is a new box on its
    // own wall, and a box has a catalogue width (`fitTrimmedToFamily`).
    if (!trimmed || b.kind === 'filler') {
      boxes.push({
        start: clear.start,
        end: clear.end,
        filler: b.kind === 'filler',
        // the sink base under it: a valance bay is kept over it
        // (`valanceBays.js`)
        ...(b.kind === 'sink-base' && b.bayId
          ? { overSinkBayId: b.bayId }
          : {}),
      });
      continue;
    }
    const fitted = fitTrimmedToFamily(
      clear,
      { lo: clippedLo, hi: clippedHi },
      keepOut,
      fillOpts
    );
    if (fitted) {
      boxes.push(...fitted.map((box) => ({ filler: false, ...box })));
    } else if (clippedLo !== clippedHi) {
      // The family sells nothing that narrow (10⅝" under Stock, whose
      // narrowest is 12"): the same offer a sub-9" remnant makes.
      slivers.push({ ...clear, farEdge: clippedLo ? clear.end : clear.start });
    }
  }
  return { boxes, slivers };
}

/**
 * A sliver too short to be a cabinet, standing against a real upper: MERGE it
 * into that upper when the two together are ONE cabinet the run's family sells
 * (user rulings 2026-09-19, "merge when it fits", and 2026-09-21, "merge only
 * if we can create the correct sizes").
 *
 * "Fits" was first read as a WIDTH — 36" or less — on the reasoning that a
 * trimmed upper already stood off the grid, so holding a merge to the grid
 * would refuse nearly every one. That reasoning was the bug: 24" + 1" made a
 * 25" cabinet nobody sells (36 of 699 generated uppers), and the trimmed ones
 * it leaned on are now family widths too (`fitTrimmedToFamily`). A merge that
 * does not land on a catalogue width is refused, and the sliver is treated as
 * what it is.
 *
 * What does not fit depends on where it stands. A CORNER sliver (`scribe`) is
 * closed with a scribe beside that upper — it dies into the corner, which is
 * where a scribe is invisible. One an OPENING clipped on a straight run is
 * dropped, as it always was: a blank panel beside a window is worse than the
 * wall. With no real upper against it, either stays bare.
 *
 * …except a CORNER sliver beside a FILLER: it joins that filler. The pair is a
 * blind corner's reveal and the leftover the lower fill put beside it, which
 * below are one filler (`cornerReveal.js`); above, the mirrored filler covers
 * the leftover and this strip the reveal's 3", and with no merge the strip
 * stood bare between the filler and the corner unit (Cabin at 2428 Lore Rd,
 * 2026-10-26; 129 generated layouts). It stays an ordinary filler, not a
 * corner piece: below it is the leg's own filler, and an upper dragged
 * across it must find it inside the stretch it may re-pack
 * (`upperBayOverrides.isEditable`). Not merged into ANY upper as a filler
 * would be — a strip beside a cabinet is the rule above.
 *
 * Returns the segment's boxes with the slivers folded in.
 */
function absorbSlivers(boxes, slivers, fillOpts) {
  let out = boxes;
  for (const sliver of slivers) {
    const atFar = (b) =>
      Math.abs(b.start - sliver.farEdge) <= SPAN_EPS ||
      Math.abs(b.end - sliver.farEdge) <= SPAN_EPS;
    const filler = sliver.scribe
      ? out.findIndex((b) => b.filler && atFar(b))
      : -1;
    if (filler >= 0) {
      out = out.map((b, i) =>
        i === filler
          ? {
              ...b,
              start: Math.min(b.start, sliver.start),
              end: Math.max(b.end, sliver.end),
            }
          : b
      );
      continue;
    }
    const idx = out.findIndex(
      (b) =>
        !b.filler &&
        (Math.abs(b.start - sliver.farEdge) <= SPAN_EPS ||
          Math.abs(b.end - sliver.farEdge) <= SPAN_EPS)
    );
    if (idx < 0) continue;
    const merged = {
      ...out[idx],
      start: Math.min(out[idx].start, sliver.start),
      end: Math.max(out[idx].end, sliver.end),
    };
    if (
      merged.end - merged.start <= maxModule(fillOpts) + SPAN_EPS &&
      isFamilyWidth(merged.end - merged.start, fillOpts)
    ) {
      out = out.map((b, i) => (i === idx ? merged : b));
    } else if (sliver.scribe && sliver.end - sliver.start >= MIN_FILLER) {
      out = [
        ...out,
        { start: sliver.start, end: sliver.end, filler: true, corner: true },
      ];
    }
  }
  return out;
}

/**
 * The upper tier's fill options: the run's family's, narrowed to the widths
 * its line can glaze when the run asks for glass (`glassUppers.glassFillOpts`,
 * plan C13). Read by every packer in this tier, so a glass run's corner
 * stretches, mirrored splits and independent spans all land on glazable
 * widths.
 */
const upperFillOpts = (run) => glassFillOpts(run, familyFillOpts(run));

/**
 * A glass run's box the line makes no glass door at — narrower than the
 * narrowest width it glazes, like a 9" Venus upper mirrored over a 9" base —
 * JOINS the neighbour it touches when the two together are a width the line
 * glazes, the smaller join first (plan C13: so every upper can be glass). One
 * with no such neighbour stays as it is, and stays solid. Fillers, the corner
 * scribe and a box an edit fixed are never joined.
 */
function joinUnglazable(boxes, fillOpts) {
  const narrowest = Math.min(...fillOpts.allowedWidthsIn) * INCHES_TO_METERS;
  const joinable = (b) => !b.filler && !b.corner && !b.fixedWidth;
  let out = [...boxes];
  for (const box of boxes) {
    if (!joinable(box) || box.end - box.start >= narrowest - SPAN_EPS) continue;
    const at = out.indexOf(box);
    if (at < 0) continue;
    const merges = out
      .filter(
        (n) =>
          n !== box &&
          joinable(n) &&
          (Math.abs(n.end - box.start) <= SPAN_EPS ||
            Math.abs(n.start - box.end) <= SPAN_EPS)
      )
      .map((n) => ({
        n,
        start: Math.min(n.start, box.start),
        end: Math.max(n.end, box.end),
      }))
      .filter(
        (m) =>
          m.end - m.start <= maxModule(fillOpts) + SPAN_EPS &&
          isFamilyWidth(m.end - m.start, fillOpts)
      )
      .sort((a, b) => a.end - a.start - (b.end - b.start));
    if (!merges.length) continue;
    const { n, start, end } = merges[0];
    out = out
      .filter((b) => b !== box)
      .map((b) => (b === n ? { ...n, start, end } : b));
  }
  return out;
}

/**
 * No family sells a wall cabinet wider than its widest module
 * (`widestModuleIn` — 36" on the 3" grid, 42" for Venus): a wider box —
 * the mirror of a 48" single-sided corner unit — is split into EQUAL modules,
 * as a tall span is (`solveEqualModuleSequence`). A remainder the grid cannot
 * absorb trails, where a tie between two like ends has always put it
 * (`fillerEnd.js`).
 */
function splitWideBox(box, fillOpts) {
  if (box.filler || box.end - box.start <= maxModule(fillOpts) + SPAN_EPS)
    return [box];
  const { modules, filler } = solveEqualModuleSequence(
    box.end - box.start,
    fillOpts
  );
  if (!modules.length) return [box];
  const out = [];
  let cursor = box.start;
  for (const m of modules) {
    out.push({ ...box, start: cursor, end: cursor + m.widthMeters });
    cursor += m.widthMeters;
  }
  if (filler) out.push({ start: cursor, end: box.end, filler: true });
  return out;
}

/** Build the upper cabinet for one planned box. */
/**
 * Where a box's bottom sits: the band's, raised for a BRIDGE cabinet's own
 * shorter height, and raised to clear the head — and the casing, the same
 * `OPENING_BUFFER` a keep-out allows sideways — of any LOW opening it stands
 * over (`upperKeepOuts(...).low`). The top stays on the run's line either
 * way, so such a box is simply the shorter one.
 */
function bottomOver(box, band, lowOpenings) {
  // A bridge cabinet (`box.height`, a user's upper override) hangs from the
  // same line and stops short; one at or over the band's own height is just
  // the band's.
  let bottomY =
    box.height > 0
      ? Math.max(band.bottomY, band.topY - box.height)
      : band.bottomY;
  for (const o of lowOpenings) {
    if (Math.min(box.end, o.hi) - Math.max(box.start, o.lo) <= SPAN_EPS)
      continue;
    bottomY = Math.max(bottomY, o.topY + OPENING_BUFFER);
  }
  return bottomY;
}

/**
 * `buildUpperBox`, and — where a box WIDER than the 3" grid's 36" cannot be
 * built at the height its slot leaves — the same span as equal boxes of 36"
 * or less, each built on its own. A family that sells wide wall cabinets
 * (Venus's 39" and 42") sells them only at full heights: a 42" upper a low
 * window cuts to 22" has no Venus size, and dropping it left the wall bare
 * where 36" boxes always found one (W3621). A user's fixed-width bay is never
 * split; a filler or open shelf is never refused in the first place.
 *
 * @param {Object} [build] `buildUpperBox`'s options, for every box built
 * @returns {{ box, cabinet }[]}
 */
function buildUpperBoxOrSplit(
  wall,
  box,
  floorY,
  band,
  lowOpenings,
  family,
  fillOpts,
  build = {}
) {
  // A box its line cannot build at its own width is fitted to the widest it
  // does, against its start (`buildUpperBox`), and what that leaves is a
  // FILLER beside it — not bare wall. Over a 23 5/8" dishwasher opening a
  // Venus run hung a 21" upper and left 2 5/8" of paint between it and the
  // next (Cabin, 2428 Lore Rd, 2026-10-26). The pair reports each piece's
  // own span; the filler carries no override and no glass.
  const fittedWithFiller = (piece, cabinet) => {
    const builtEnd = piece.start + (cabinet.dimensions?.x ?? 0);
    const rest = piece.end - builtEnd;
    if (piece.filler || piece.openShelf || piece.blind || rest < MIN_FILLER)
      return [{ box: piece, cabinet }];
    const fitted = { ...piece, end: builtEnd };
    const fillerBox = { start: builtEnd, end: piece.end, filler: true };
    const filler = buildUpperBox(
      wall,
      fillerBox,
      floorY,
      band,
      lowOpenings,
      family,
      build
    );
    return filler
      ? [
          { box: fitted, cabinet },
          { box: fillerBox, cabinet: filler },
        ]
      : [{ box: fitted, cabinet }];
  };
  const one = buildUpperBox(
    wall,
    box,
    floorY,
    band,
    lowOpenings,
    family,
    build
  );
  // …and a rack the short cabinet stacked with it (`rackStacks.js`)
  const companion =
    one && box.rack
      ? buildRackCompanion(wall, box, floorY, band, lowOpenings, family)
      : null;
  if (companion) {
    // it names the rack it stands with, as a stacked box names the upper
    // under it (`stackOf`): the overlap check and the panel read the pair by
    // it
    companion.rackOf = one.id;
    return [
      ...fittedWithFiller(box, one),
      { box: { ...box, rackCompanion: true }, cabinet: companion },
    ];
  }
  if (one) return fittedWithFiller(box, one);
  const wideIn = (box.end - box.start) / INCHES_TO_METERS;
  if (
    box.filler ||
    box.openShelf ||
    box.valance ||
    box.rack ||
    box.fixedWidth ||
    wideIn <= MAX_MODULE_IN
  )
    return [];
  const narrow = (fillOpts?.allowedWidthsIn || []).filter(
    (w) => w <= MAX_MODULE_IN
  );
  const { modules } = solveEqualModuleSequence(box.end - box.start, {
    ...fillOpts,
    allowedWidthsIn: narrow,
  });
  const out = [];
  let start = box.start;
  for (const m of modules) {
    const piece = { ...box, start, end: start + m.widthMeters };
    start = piece.end;
    const cabinet = buildUpperBox(
      wall,
      piece,
      floorY,
      band,
      lowOpenings,
      family,
      build
    );
    if (cabinet) out.push(...fittedWithFiller(piece, cabinet));
  }
  return out;
}

/** Is this planned box a filler touching a floating-shelf bay? */
function fillerBesideShelves(box, boxes) {
  if (!box.filler) return false;
  return boxes.some(
    (o) =>
      o.floatingShelves &&
      !o.none &&
      (Math.abs(o.end - box.start) <= SPAN_EPS ||
        Math.abs(o.start - box.end) <= SPAN_EPS)
  );
}

/**
 * How a rack box stands in a band `heightM` tall (`rackStacks.rackStackPlan`),
 * from what its record asks (`rackCabinet`), or null when it does not fit.
 */
function rackPlanOf(box, heightM, family) {
  return rackStackPlan(
    family,
    box.rack,
    (box.end - box.start) / INCHES_TO_METERS,
    heightM / INCHES_TO_METERS,
    box.override?.rackCabinet ?? null
  );
}

/**
 * The short wall cabinet stacked with a rack (`rackStacks.js`): over it on
 * the top line, or under it on the bottom line — an upper the line makes,
 * wearing the rack's look, `category.rackCompanion` saying which. It answers
 * to no record of its own: the rack's says where it stands and its front —
 * doors, an open shelf (here) or glass doors (`rackCabinetGlass`). Null when
 * the rack stands alone.
 */
function buildRackCompanion(wall, box, floorY, band, lowOpenings, family) {
  if (!RACKS[box.rack]) return null;
  const bottomY = bottomOver(box, band, lowOpenings);
  const stack = rackPlanOf(box, band.topY - bottomY, family);
  if (!stack || stack.position === 'none') return null;
  const rackM = stack.rackIn * INCHES_TO_METERS;
  const height = stack.cabinetIn * INCHES_TO_METERS;
  const cabinet = buildUpperCabinetForSegment(
    wall,
    { start: box.start, end: box.end },
    floorY,
    {
      height,
      bottomY: stack.position === 'above' ? bottomY + rackM : bottomY,
      // built on the rack's own span, so its stamp would be the rack's: a
      // tag of its own, or the two share an id and the scene, which draws by
      // id, left the old pair standing over a new layout (the user,
      // 2026-11-01) — the stacked row's `stack` tag, for the same reason
      stampTag: 'rack_cabinet',
    }
  );
  if (!cabinet) return null;
  cabinet.category = { 'upper-cabinet': true, rackCompanion: stack.position };
  if (rackCabinetFrontOf(box.override) === 'open') {
    // the box with no doors on it, as an upper's open shelf is
    cabinet.label = 'Open Shelf';
    cabinet.category.openShelf = true;
    cabinet.columns = [{ type: 'open-shelf', width: cabinet.dimensions.x }];
  }
  return cabinet;
}

/**
 * Does the cabinet stacked with a rack get glass doors? When the rack's
 * record asks (`rackCabinetFront: 'glass'`) and the line makes a glass door
 * for it at its size, in the look it wears — `{ on, reason }`, the reason
 * why not when asked and refused.
 */
function rackCabinetGlass(run, record, cabinet, ctx) {
  if (rackCabinetFrontOf(record) !== 'glass')
    return { on: false, reason: null };
  const look = effectiveAppearance({ override: record, run, ctx }) || {};
  const reason = glassUnavailableReason(run, cabinet, look);
  return { on: !reason, reason };
}

/** What each rack is called on the drawing and in a list (`racks.js`). */
const RACK_LABELS = Object.freeze({
  'wine-rack': 'Wine Rack',
  'wine-x': 'Wine Rack with an X',
  'plate-rack': 'Plate Rack',
  'wine-cubes': 'Wine Cubes',
});

/**
 * Build the upper cabinet for one planned box.
 *
 * @param {Object} [opts]
 * @param {boolean} [opts.sitOnBottom] the box STANDS on the band's bottom
 *   line and a vendor height snap lowers its top — a stacked row on the
 *   uppers (`upperStack.js`); otherwise it hangs from the top line and the
 *   snap raises its bottom
 * @param {string} [opts.stampTag] a tag in the cabinet's id stamp
 *   (`buildUpperCabinetForSegment`)
 * @param {boolean} [opts.offeredHeights] snap to a height the family OFFERS
 *   in every family (`offeredUpperHeightIn` — the stock heights with no size
 *   catalog), not only a catalog family's (a stacked row)
 */
function buildUpperBox(
  wall,
  box,
  floorY,
  band,
  lowOpenings,
  family = null,
  opts = {}
) {
  if (box.valance)
    return withBoxRecord(buildValanceBay(wall, box, floorY, band, family), box);
  let bottomY = bottomOver(box, band, lowOpenings);
  let height = band.topY - bottomY;
  if (height <= SPAN_EPS) return null; // no band left above the opening
  // A vendor family hangs only heights it makes (design 2026-10-05, D3): the
  // tallest that fits the slot, still on the run's line — or no box at all.
  // Fillers, open shelves and floating shelves are not sold boxes and keep
  // the slot (a floating shelf's own length rule is `floatingShelves.js`).
  // A wall cabinet with drawers stands on the counter and rises to the
  // band's top (`wallDrawers.js`): its own height, never snapped.
  const wallDrawers = box.wallDrawers && band.counterTopY != null;
  if (wallDrawers) {
    bottomY = band.counterTopY;
    height = band.topY - bottomY;
  }
  // A rack stands at its own height (`racks.js`), at the width it comes in —
  // never a wall cabinet size: on the bottom line under the short cabinet
  // stacked with it, else from the top line (`rackStacks.js`).
  const rack = RACKS[box.rack] ? box.rack : null;
  const stack = rack ? rackPlanOf(box, height, family) : null;
  if (rack) {
    if (!stack) return null;
    height = stack.rackIn * INCHES_TO_METERS;
    if (stack.position !== 'above') bottomY = band.topY - height;
  }
  if (
    !box.filler &&
    !box.openShelf &&
    !box.floatingShelves &&
    !wallDrawers &&
    !rack
  ) {
    const kind = box.blind ? 'wall-blind' : 'wall';
    // A box a slot or a run end cut to a width the family does not make is
    // fitted to the widest it does, against its start.
    const fitW = fitUpperWidthIn(
      family,
      kind,
      (box.end - box.start) / INCHES_TO_METERS
    );
    if (fitW === null) return null;
    if (fitW !== undefined)
      box = { ...box, end: box.start + fitW * INCHES_TO_METERS };
    const snapped = (
      opts.offeredHeights ? offeredUpperHeightIn : snapUpperHeightIn
    )(
      family,
      kind,
      (box.end - box.start) / INCHES_TO_METERS,
      height / INCHES_TO_METERS
    );
    if (snapped === null) return null;
    if (snapped !== undefined) {
      height = snapped * INCHES_TO_METERS;
      if (!opts.sitOnBottom) bottomY = band.topY - height;
    }
  }
  const cabinet = buildUpperCabinetForSegment(
    wall,
    { start: box.start, end: box.end },
    floorY,
    {
      height,
      bottomY,
      ...(wallDrawers
        ? { depth: WALL_DRAWERS.depthIn * INCHES_TO_METERS }
        : {}),
      ...(opts.stampTag ? { stampTag: opts.stampTag } : {}),
    }
  );
  if (!cabinet) return null;
  // A filler over a lower filler keeps the upper run continuous; render it as
  // a blank false-panel (no door / handle), mirroring the lower filler. The
  // flat doorStyle is forced after the appearance pass in `fillRun`.
  if (box.filler) {
    cabinet.label = 'Upper Cabinet Filler';
    cabinet.category = { 'upper-cabinet': true, filler: true };
    cabinet.columns = [{ type: 'false-panel', width: cabinet.dimensions.x }];
  } else if (box.floatingShelves) {
    // Boards on the wall where a box would hang (a user's upper override,
    // `floatingShelves.js`): no carcass, no doors — `cabinetFrontLayout`
    // lays the boards out from this column.
    cabinet.label = 'Floating Shelves';
    cabinet.category = { 'upper-cabinet': true, floatingShelves: true };
    cabinet.columns = [
      { type: 'floating-shelves', width: cabinet.dimensions.x },
    ];
  } else if (wallDrawers) {
    // A door over two drawers, standing on the counter (a user's upper
    // override, `wallDrawers.js`; Venus WD18xx).
    cabinet.label = 'Wall Cabinet with Drawers';
    cabinet.category = { 'upper-cabinet': true, wallDrawers: true };
    cabinet.columns = [
      {
        type: 'door-over-drawers',
        width: cabinet.dimensions.x,
        drawerCount: WALL_DRAWERS.drawers,
      },
    ];
  } else if (rack) {
    // An open rack (a user's upper override, `racks.js`): the open shelf's
    // shell holding its pattern — `cabinetFrontLayout` lays it out from this
    // column — and where its short cabinet stands, when it has one.
    cabinet.label = RACK_LABELS[rack];
    cabinet.category = { 'upper-cabinet': true, [RACKS[rack].category]: true };
    cabinet.columns = [{ type: rack, width: cabinet.dimensions.x }];
    if (stack.position !== 'none') cabinet.rackStack = stack.position;
  } else if (box.microwaveShelf) {
    // Doors over an open microwave shelf (a user's upper override,
    // `microwaveCabinets.js`); the fill puts the microwave on it.
    cabinet.label = 'Microwave Wall Cabinet';
    cabinet.category = { 'upper-cabinet': true, microwaveShelf: true };
    cabinet.columns = [
      { type: 'microwave-shelf', width: cabinet.dimensions.x },
    ];
  } else if (box.endShelf) {
    // Open shelves round the run's open end (a user's upper override,
    // `endShelves.js`); the fill stamps which side is open.
    cabinet.label = 'Wall End Shelf';
    cabinet.category = { 'upper-cabinet': true, endShelf: true };
    cabinet.columns = [{ type: 'end-shelf', width: cabinet.dimensions.x }];
  } else if (box.angledEnd) {
    // The run's end box cut at 45°, its door on the diagonal (a user's upper
    // override, `angledEnds.js`; Venus WAE12xx); the fill stamps which side
    // is open.
    cabinet.label = 'Angled End Wall Cabinet';
    cabinet.category = { 'upper-cabinet': true, angledEnd: true };
    cabinet.columns = [{ type: 'angled-end', width: cabinet.dimensions.x }];
  } else if (box.openShelf) {
    // The same box with no doors on it (a user's upper override). The renderer
    // draws the shell and its shelves from this column — `cabinetFrontLayout`.
    cabinet.label = 'Open Shelf';
    cabinet.category = { 'upper-cabinet': true, openShelf: true };
    cabinet.columns = [{ type: 'open-shelf', width: cabinet.dimensions.x }];
  } else if (box.blind) {
    // The other leg's cabinet stands across part of this face
    // (`markBlindCornerBoxes`).
    cabinet.label = 'Blind Corner Wall Cabinet';
    cabinet.category = { 'upper-cabinet': true, blindCorner: true };
    cabinet.columns = blindColumns(cabinet.dimensions.x, box.blind);
  }
  return withBoxRecord(cabinet, box);
}

/** What every built upper carries from the box it was planned as. */
function withBoxRecord(cabinet, box) {
  if (!cabinet) return null;
  // A corner stretch's scribe is the corner plan's, not a bay the panel may
  // offer a width or a front for (`upperBayOverrides.isEditable`).
  if (box.corner) cabinet.category.cornerPiece = true;
  if (box.fixedWidth) cabinet.category.fixedWidth = true;
  // Tagged with the override that governs it, so a later edit updates the
  // same record — a slid fixed bay's centre is not its anchor.
  if (box.override) cabinet.overrideId = box.override.id;
  // the sink base under it, which a valance picked here is kept over
  if (box.overSinkBayId) cabinet.overSinkBayId = box.overSinkBayId;
  return cabinet;
}

/**
 * A valance bay (`valanceBays.js`): no cabinet, the valance board's own box —
 * the bay's width, the board's height under the uppers' top, the board's
 * depth with its face on the uppers' front line. What is drawn and billed is
 * the valance `cabinet-trim/valances.js` hangs in the gap it leaves. Null
 * when the line makes no valance of its style.
 */
function buildValanceBay(wall, box, floorY, band, family) {
  const style = box.valanceStyle || 'arched';
  const board = valanceBoardOf(family, style);
  if (!board) return null;
  const span = { start: box.start, end: box.end };
  const bottomY = band.topY - board.heightM;
  const cabinet = buildUpperCabinetForSegment(wall, span, floorY, {
    height: board.heightM,
    bottomY,
  });
  if (!cabinet) return null;
  // forward from the uppers' back line to their front, less the board
  const forward = (cabinet.dimensions.z - board.depthM) / 2;
  const ry = cabinet.rotation?.y || 0;
  cabinet.center = {
    ...cabinet.center,
    x: cabinet.center.x + Math.sin(ry) * forward,
    z: cabinet.center.z + Math.cos(ry) * forward,
  };
  cabinet.dimensions = { ...cabinet.dimensions, z: board.depthM };
  cabinet.label = 'Valance';
  cabinet.category = { 'upper-cabinet': true, valanceBay: true };
  cabinet.columns = [{ type: 'valance', width: cabinet.dimensions.x }];
  cabinet.valanceStyle = style;
  return cabinet;
}

/**
 * A box's own height, held to what its line makes (plan 2026-10-21, Task
 * 1.2). A stored bay height the line cannot build at this box's width — or
 * one the ceiling has no room for — is dropped from the box, so it is built at
 * the run's height, ONE box, and the reason is returned for a warning. It used
 * to reach the vendor snap as a short slot: a 42"-wide Venus upper asked for
 * 15" was split into two 21" boxes, a 9" one built nothing at all, and a
 * 15"-wide one asked for 18" was quietly built 15". A height at or above the
 * run's is the run's height and is dropped silently.
 *
 * The box is asked about at the width the fill will build it
 * (`fitUpperWidthIn`), as the panel asks about the built box.
 */
function heldToLine(run, planned, band, glass = false) {
  if (!(planned.height > 0)) return { box: planned, warning: null };
  const probe = plannedProbe(run, planned, band, { glass });
  const { heightM, reason } = bayHeightFor(run, probe, band, planned.height);
  if (heightM != null) return { box: planned, warning: null };
  const { height: _dropped, ...box } = planned;
  return {
    box,
    warning: reason
      ? {
          severity: 'warning',
          type: 'upper-height',
          ...(planned.override?.id ? { overrideId: planned.override.id } : {}),
          message: reason,
        }
      : null,
  };
}

/**
 * A stand-in for the box the fill will build from `planned`, at the band's
 * height and the width the fill will fit it to (`fitUpperWidthIn`), so the
 * height and glass rules ask about the box as it will be built.
 */
function plannedProbe(run, planned, band, { glass = false } = {}) {
  const family = familyOf(run);
  const kind = planned.blind ? 'wall-blind' : 'wall';
  const widthIn = (planned.end - planned.start) / INCHES_TO_METERS;
  const fitW = fitUpperWidthIn(family, kind, widthIn);
  return upperProbe(
    fitW > 0 ? fitW * INCHES_TO_METERS : planned.end - planned.start,
    band.height,
    {
      blind: !!planned.blind,
      filler: !!planned.filler,
      openShelf: !!planned.openShelf,
      floatingShelves: !!planned.floatingShelves,
      endShelf: !!planned.endShelf,
      glass,
    }
  );
}

/**
 * Does this planned box get GLASS doors (plan C11–C16)? Asked by the record
 * it answers to (`glass`), else by the run (`upperSpec.glass`), and allowed
 * by `glassUnavailableReason` for the box at the height it will be built —
 * its own height when the line makes it, never resized to suit glass — and
 * in the look the run gives it. A filler, an open shelf and the corner
 * scribe ignore glass.
 *
 * @returns {{ on: boolean, reason: string|null, asked: boolean }} `asked` —
 *   the box's own record asked (its refusal is its own warning); otherwise a
 *   refusal counts toward the run's one "N uppers stay solid" warning
 */
function glassFor(run, planned, ctx) {
  const none = { on: false, reason: null, asked: false };
  if (
    planned.filler ||
    planned.openShelf ||
    planned.floatingShelves ||
    planned.endShelf ||
    planned.angledEnd ||
    planned.microwaveShelf ||
    planned.wallDrawers ||
    planned.valance ||
    planned.rack ||
    planned.corner
  )
    return none;
  const record = planned.override || planned.seatedRecord || null;
  if (!wantsGlass(run, record)) return none;
  const asked = record?.glass === true;
  const probe = plannedProbe(run, planned, ctx.upperBand);
  // At the height the box will be BUILT: its own when the line makes it
  // (a bridge cabinet keeps its height and stays solid if there is no glass
  // door for it — a choice is never resized to suit glass), else the run's.
  const own =
    planned.height > 0
      ? bayHeightFor(run, probe, ctx.upperBand, planned.height).heightM
      : null;
  const builtIn =
    own != null
      ? own / INCHES_TO_METERS
      : runUpperHeightIn(run, probe, ctx.upperBand);
  if (builtIn == null) return none;
  probe.dimensions = { ...probe.dimensions, y: builtIn * INCHES_TO_METERS };
  const look = effectiveAppearance({ override: record, run, ctx }) || {};
  const reason = glassUnavailableReason(run, probe, look);
  return { on: !reason, reason, asked };
}

/**
 * The run's own height, when the ceiling has no room for it (plan C6): the
 * band was cut to the tallest height the line makes (`upperBandFor`, or the
 * per-width snap in `buildUpperBox`), and the run says so once.
 */
function runHeightWarning(band) {
  const roomIn = roomInches(band.room);
  if (fitsUnderCeiling(roomIn, band.requested / INCHES_TO_METERS)) return null;
  return {
    severity: 'warning',
    type: 'upper-height',
    message: ceilingReason(roomIn),
  };
}

/**
 * Upper-cabinet pass over the wall-backed segments. Free-standing legs get no
 * uppers (the caller passes only wall-backed segments), and a peninsula
 * segment has no wall above it: `run.uppers === false` says that for a run
 * that is ALL peninsula; a run that turns from a real wall onto an arm keeps
 * its uppers on the wall and hangs none over the arm. Gated by the caller on
 * run.uppers !== false.
 *
 * @param {Object} lower the lower tier's corner decisions — see
 *   `planUpperCorners`
 * @returns {{ uppers: Object[], upperBays: {segIndex, start, end, cabinet}[],
 *   lCorners: Map<number, {reachA, reachB}>, warnings: Object[] }} every upper emitted, in emit order, BEFORE
 *   `dropLoneFillerUppers` — the caller tags them in this order, so a dropped
 *   filler still consumes its bay index exactly as it always has — and the
 *   corners that carry an upper L (`buildCornerUppers`).
 */
function fillUppers(run, ctx, lower, lowerBays) {
  const uppers = [];
  const upperBays = [];
  const warnings = [];
  // why each box the run's glass switch asked for stayed solid
  const stayedSolid = [];
  const hoodSky = hoodSkySpans(run, ctx.existingObjects, lowerBays);
  const keepOutsBySeg = run.segments.map((_, i) =>
    upperKeepOuts(run, i, ctx, lowerBays, hoodSky)
  );
  const plan = planUpperCorners(run, ctx, lower, keepOutsBySeg);
  // 'lowers' (or absent): mirror the lower bays. 'independent': the tier is
  // packed on its own (`upperSpec.align`, user ruling 2026-09-19).
  const independent = run.upperSpec?.align === 'independent';
  const atCorners = independent
    ? null
    : cornerBoxesFor(run, plan, lower, keepOutsBySeg);
  const fillOpts = upperFillOpts(run);
  // A glass run's mirrored box its line makes no glass door at the width of
  // (narrower than the narrowest it glazes) joins a neighbour (C13).
  const joinsUnglazable =
    run.upperSpec?.glass === true && !!glazableWidthsIn(familyOf(run))?.length;

  // Planned for EVERY segment before any is built: whether a box is blind
  // depends on what the other leg of its corner holds.
  const editedBySeg = run.segments.map(() => null);
  for (let i = 0; i < run.segments.length; i += 1) {
    const { wall } = run.segments[i];
    if (wall?.isVirtual) continue;
    // Where each width-less record was worn last solve: it is seated on that
    // box's successor, not on whatever lands under its stored point
    // (`overrideHomes.js`).
    const prevSpans = previousSpansOf(run, i, 'upper', ctx.existingObjects);
    let edited;
    if (independent) {
      // Packed on its own: every clear span of the band, as equal modules,
      // the user's fixed widths among them. Nothing is mirrored.
      edited = packIndependentSpans(
        run,
        i,
        independentSpans(run, i, plan, lower, keepOutsBySeg[i]),
        fillOpts,
        prevSpans
      );
    } else {
      // A mirrored upper gives way to what the corner took of its wall — an
      // L's leg, or the 12" another leg's body stands against.
      const mirrored = mirroredBoxes(
        run,
        i,
        lower,
        lowerBays,
        [...keepOutsBySeg[i].openings, ...plan.claims[i]],
        fillOpts
      );
      const packedBoxes = [
        ...absorbSlivers(
          mirrored.boxes,
          [...mirrored.slivers, ...atCorners.slivers[i]],
          fillOpts
        ),
        ...atCorners.boxes[i],
      ].flatMap((box) => splitWideBox(box, fillOpts));
      const planned = joinsUnglazable
        ? joinUnglazable(packedBoxes, fillOpts)
        : packedBoxes;
      // The user's own edits to this tier's bays, folded in last: they edit
      // the PLAN, so an untouched run never reaches this at all.
      edited = applyUpperOverrides(run, i, planned, fillOpts, prevSpans);
    }
    // a filler from each wood hood's side out to the uppers beside it,
    // which stand clear of its moldings (`hoodClearance.js`)
    const woodHoods = hoodSky.filter((h) => h.box && h.segIndex === i);
    editedBySeg[i] = woodHoods.length
      ? {
          ...edited,
          boxes: withHoodClearance(
            edited.boxes,
            woodHoods,
            (width) => widestModuleWithin(width, fillOpts) > 0
          ),
        }
      : edited;
  }
  markBlindCornerBoxes(
    run,
    plan,
    editedBySeg.map((e) => e?.boxes || [])
  );

  for (let i = 0; i < run.segments.length; i += 1) {
    const edited = editedBySeg[i];
    if (!edited) continue;
    const { wall } = run.segments[i];
    warnings.push(...edited.warnings);
    for (const asPlanned of edited.boxes) {
      if (asPlanned.none) continue; // "no cabinet here" — the wall stays bare
      // A filler closes the gap between a BOX and what stops it; floating
      // shelves need no scribe — they simply stop — so a filler beside them
      // would stand alone on the wall like a post (the user, 2026-10-31).
      if (fillerBesideShelves(asPlanned, edited.boxes)) continue;
      const glass = glassFor(run, asPlanned, ctx);
      if (glass.reason && glass.asked) {
        const record = asPlanned.override || asPlanned.seatedRecord;
        warnings.push({
          severity: 'warning',
          type: 'upper-glass',
          ...(record?.id ? { overrideId: record.id } : {}),
          message: glass.reason,
        });
      } else if (
        glass.reason &&
        !isSilentGlassRefusal(
          plannedProbe(run, asPlanned, ctx.upperBand),
          glass.reason
        )
      ) {
        stayedSolid.push(glass.reason);
      }
      const { box: planned, warning } = heldToLine(
        run,
        asPlanned.valance
          ? {
              ...asPlanned,
              valanceStyle: valanceStyleFor(run, asPlanned.override),
            }
          : asPlanned,
        ctx.upperBand,
        glass.on
      );
      if (warning) warnings.push(warning);
      const built = buildUpperBoxOrSplit(
        wall,
        planned,
        ctx.floorY,
        ctx.upperBand,
        keepOutsBySeg[i].low,
        familyOf(run),
        fillOpts
      );
      for (const { box, cabinet } of built) {
        uppers.push(cabinet);
        // the cabinet stacked with a rack takes glass from the rack's record
        const boxGlass = box.rackCompanion
          ? rackCabinetGlass(run, box.override, cabinet, ctx)
          : glass;
        if (box.rackCompanion && boxGlass.reason)
          warnings.push({
            severity: 'warning',
            type: 'upper-glass',
            ...(box.override?.id ? { overrideId: box.override.id } : {}),
            message: boxGlass.reason,
          });
        // Its own span, so it can resolve its OWN per-bay override. An upper
        // and the lower under it share a wall param; only the tier separates
        // them.
        upperBays.push({
          segIndex: i,
          start: box.start,
          end: box.end,
          cabinet,
          // The record this box answers to: a fixed-width bay's own wherever
          // it slid to, and the width-less record the override pass seated on
          // it (a front or height as `override`, an appearance alone as
          // `seatedRecord`) — so the appearance pass reads the same one.
          ...(box.override || box.seatedRecord
            ? { override: box.override || box.seatedRecord }
            : {}),
          // drawn with glass doors after the appearance pass (`fillRun`)
          ...(boxGlass.on && !box.filler ? { glass: true } : {}),
        });
      }
    }
  }
  const runWarning = uppers.length ? runHeightWarning(ctx.upperBand) : null;
  if (runWarning) warnings.push(runWarning);
  const solid = solidUppersWarning(run, stayedSolid);
  if (solid) warnings.push(solid);
  for (const c of plan.corners) {
    if (c.honoured === false)
      warnings.push({
        severity: 'warning',
        type: 'corner-treatment',
        cornerIndex: c.cornerIndex,
        message:
          'The upper corner chosen here no longer fits, so the layout decided it.',
      });
    if (c.size?.honoured === false)
      warnings.push({
        severity: 'warning',
        type: 'corner-treatment',
        cornerIndex: c.cornerIndex,
        message:
          'The blind corner size chosen here no longer fits, so the layout sized it.',
      });
  }
  return {
    uppers,
    upperBays,
    lCorners: plan.lCorners,
    corners: plan.corners,
    warnings,
  };
}

/**
 * Slack for "these two cabinets touch". The fill packs flush, so real
 * neighbours meet at 0; this only has to absorb float, not a real reveal —
 * a gap wide enough to see is a gap.
 */
const ABUT_EPS = 0.002; // 2 mm

/** Is this one of the blank false-panels a filler bay gets? */
const isFillerUpper = (c) => !!c?.category?.filler;

/** Do two emitted cabinets touch, along the wall they share? */
const upperCabinetsAbut = (a, b) => {
  const gap =
    Math.hypot(a.center.x - b.center.x, a.center.z - b.center.z) -
    ((a.dimensions?.x || 0) + (b.dimensions?.x || 0)) / 2;
  return gap < ABUT_EPS;
};

/**
 * A filler upper exists for exactly one reason: to keep the upper run
 * continuous with the lower one, matching the width of the filler below it. So
 * one that ends up with no REAL upper beside it is a hairline hanging on its
 * own — 2.8" of cabinet next to a bare wall.
 *
 * It is emitted before its neighbours' fate is known, and two rules can take
 * those neighbours away after the fact, neither of them wrong on its own:
 *
 *  - an OPENING trims the bay next door below `MIN_CABINET_WIDTH` and that
 *    candidate is dropped, while the filler — untrimmed, and so exempt from
 *    the minimum by the rule right above — survives;
 *  - the bay next door is a SINK-BASE (or a range, fridge, tall unit), which
 *    the candidate list skips outright because something else owns that sky.
 *
 * Measured over 1500 dev rooms: 55 filler uppers, 53 abutting a real upper,
 * 0 abutting only another filler, and exactly 2 alone — one from each cause.
 * So this drops those two and nothing else.
 *
 * Deliberately a POST-pass rather than a condition inside the loop: whether a
 * filler has a neighbour is not knowable until every candidate has been
 * resolved, and the exemption above is right for the case it was written for
 * (a genuinely narrow bay whose upper has always been drawn).
 *
 * @param {{ uppers: Object[], upperBays: Object[] }} emitted `fillUppers`' result
 * @returns {{ uppers: Object[], upperBays: Object[] }} the same, minus the lone fillers
 */
function dropLoneFillerUppers({ uppers, upperBays }) {
  const lone = new Set();
  for (const u of uppers) {
    if (!isFillerUpper(u)) continue;
    const hasRealNeighbour = uppers.some(
      (o) => o !== u && !isFillerUpper(o) && upperCabinetsAbut(u, o)
    );
    if (!hasRealNeighbour) lone.add(u);
  }
  if (!lone.size) return { uppers, upperBays };
  return {
    uppers: uppers.filter((u) => !lone.has(u)),
    upperBays: upperBays.filter((b) => !lone.has(b.cabinet)),
  };
}

module.exports = {
  fillUppers,
  dropLoneFillerUppers,
  buildUpperBoxOrSplit,
  splitWideBox,
  planUpperCorners,
  packCornerStretch,
  fitTrimmedToFamily,
  widestGap,
  UPPER_DEPTH,
};
